/**
 * Custom hook for real-time collaborative editing via WebSocket.
 * Handles WebSocket connection, document changes, cursor updates, and presence.
 * 
 * Features:
 * - Automatic reconnection on disconnect
 * - Operational transform for conflict-free editing
 * - Cursor position tracking
 * - Presence awareness (who's online)
 * - Error handling and recovery
 */

import { useEffect, useRef, useState, useCallback } from 'react'
import { log, logError, logWarn, logDebug } from '@utils/logger'

// Message types for WebSocket communication
type CollaborationMessageType = 
  | 'connect' 
  | 'disconnect' 
  | 'change' 
  | 'cursor' 
  | 'presence' 
  | 'error' 
  | 'pong'

interface CollaborationMessage {
  type: CollaborationMessageType
  document_id?: string
  user_id?: string
  content?: string
  change?: {
    type: 'insert' | 'delete' | 'retain'
    position: number
    length: number
    content?: string
    version: number
  }
  position?: number
  username?: string
  users?: Array<{
    user_id: string
    username: string
    full_name?: string
    cursor_position?: number
    connected_at?: string
    last_seen_at?: string
  }>
  version?: number
  timestamp?: string
  message?: string
  code?: string
}

interface UseCollaborationOptions {
  documentId: string | null
  enabled?: boolean
  onContentChange?: (content: string) => void
  onCursorUpdate?: (userId: string, position: number, username: string) => void
  onPresenceUpdate?: (users: Array<any>) => void
  onError?: (error: string) => void
}

interface UseCollaborationReturn {
  isConnected: boolean
  isConnecting: boolean
  activeUsers: Array<any>
  sendChange: (change: { type: 'insert' | 'delete'; position: number; length: number; content?: string }, content: string) => void
  sendCursorUpdate: (position: number) => void
  disconnect: () => void
}

/**
 * Hook for managing WebSocket connection for collaborative editing.
 * 
 * @param options - Configuration options
 * @returns Collaboration state and methods
 */
export function useCollaboration(options: UseCollaborationOptions): UseCollaborationReturn {
  const {
    documentId,
    enabled = true,
    onContentChange,
    onCursorUpdate,
    onPresenceUpdate,
    onError
  } = options

  // SECURITY: WebSocket authentication now uses HttpOnly cookies
  // Token is no longer stored in localStorage or passed as query parameter
  // Backend will read token from cookie automatically
  
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const isConnectingRef = useRef(false) // Prevent multiple simultaneous connection attempts
  const shouldReconnectRef = useRef(true) // Control reconnection behavior
  const mountedRef = useRef(false) // Track if component is mounted
  const connectionKeyRef = useRef<string>('') // Track current connection key (documentId+enabled)
  
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [activeUsers, setActiveUsers] = useState<Array<any>>([])

  // Get WebSocket URL
  // SECURITY: No longer requires token in query string - backend reads from cookie
  const getWebSocketUrl = useCallback((docId: string): string => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
    const host = window.location.host
    const baseUrl = import.meta.env.VITE_API_URL || ''

    // Handle relative URLs (for Tailscale/proxy) - same logic as API client
    const isTailscaleIP = /^https?:\/\/100\.\d{1,3}\.\d{1,3}\.\d{1,3}/.test(window.location.origin)
    const isTailscaleAccess =
      window.location.origin.includes('.ts.net') ||
      window.location.origin.includes('tailscale') ||
      isTailscaleIP
    const isLocalhostAPI =
      baseUrl && (baseUrl.includes('localhost') || baseUrl.includes('127.0.0.1'))

    let wsHost: string

    // Map frontend → backend domains explicitly for SciDraft environments
    // NOTE: Dev frontend now uses production API to avoid cookie conflicts
    if (host === 'dev.scidraft.com') {
      wsHost = 'api.scidraft.com'  // Dev frontend uses production API
    } else if (host === 'staging.scidraft.com') {
      wsHost = 'api-staging.scidraft.com'
    } else if (host === 'app.scidraft.com' || host === 'scidraft.com') {
      wsHost = 'api.scidraft.com'
    } else if (isTailscaleAccess && isLocalhostAPI) {
      // Use relative path for Tailscale proxy
      wsHost = host
    } else if (!baseUrl || isTailscaleAccess) {
      // Use relative path
      wsHost = host
    } else {
      // Use absolute URL from env
      wsHost = baseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')
    }

    // SECURITY: Token is now in HttpOnly cookie, not query parameter
    // Backend will read from cookie automatically
    // For backward compatibility, token query param is optional
    return `${protocol}//${wsHost}/api/ws/documents/${docId}`
  }, [])

  // Handle incoming WebSocket messages
  const handleMessage = useCallback((message: CollaborationMessage) => {
    switch (message.type) {
      case 'connect':
        // Initial connection - receive document state
        if (message.content !== undefined && message.version !== undefined) {
          logDebug('[useCollaboration] Received initial document state:', {
            contentLength: message.content.length,
            version: message.version
          })
          onContentChange?.(message.content)
        }
        
        // Update active users
        if (message.users) {
          setActiveUsers(message.users)
          onPresenceUpdate?.(message.users)
        }
        break

      case 'change':
        // Document content changed
        if (message.content !== undefined) {
          logDebug('[useCollaboration] Received content change:', {
            changeType: message.change?.type,
            position: message.change?.position,
            length: message.change?.length,
            version: message.version
          })
          onContentChange?.(message.content)
        }
        break

      case 'cursor':
        // Cursor position updated
        if (message.user_id && message.position !== undefined && message.username) {
          onCursorUpdate?.(message.user_id, message.position, message.username)
        }
        break

      case 'presence':
        // Presence update (who's online)
        if (message.users) {
          setActiveUsers(message.users)
          onPresenceUpdate?.(message.users)
        }
        break

      case 'error':
        logError('[useCollaboration] Server error:', message.message, message.code)
        onError?.(message.message || 'Unknown error')
        break

      case 'pong':
        // Heartbeat response - connection is alive
        break

      default:
        logWarn('[useCollaboration] Unknown message type:', message.type)
    }
  }, [onContentChange, onCursorUpdate, onPresenceUpdate, onError])

  // Connect to WebSocket
  const connect = useCallback(() => {
    // SECURITY: No longer check for token - cookies are sent automatically by browser
    // Backend will reject connection if cookie is missing or invalid
    
    // Prevent multiple simultaneous connection attempts
    if (!documentId || !enabled) {
      return
    }
    
    // CRITICAL: Check if already connecting or connected BEFORE setting flag
    if (isConnectingRef.current) {
      logDebug('[useCollaboration] Already connecting, skipping...')
      return
    }
    
    // Don't connect if already connected or connecting
    const currentState = wsRef.current?.readyState
    if (currentState === WebSocket.OPEN || currentState === WebSocket.CONNECTING) {
      logDebug('[useCollaboration] Already connected/connecting, skipping. State:', currentState)
      return
    }

    // Set flag IMMEDIATELY to prevent race conditions
    isConnectingRef.current = true
    setIsConnecting(true)
    
    const wsUrl = getWebSocketUrl(documentId)
    
    logDebug('[useCollaboration] Connecting to WebSocket:', wsUrl.replace(/token=[^&]+/, 'token=***'))

    try {
      const ws = new WebSocket(wsUrl)
      wsRef.current = ws

      ws.onopen = () => {
        logDebug('[useCollaboration] WebSocket connected')
        setIsConnected(true)
        setIsConnecting(false)
        
        // Start ping interval (keep connection alive)
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({
              type: 'ping',
              timestamp: new Date().toISOString()
            }))
          }
        }, 30000) // Ping every 30 seconds
      }

      ws.onmessage = (event) => {
        try {
          const message: CollaborationMessage = JSON.parse(event.data)
          handleMessage(message)
        } catch (error) {
          logError('[useCollaboration] Error parsing message:', error)
        }
      }

      ws.onerror = (error) => {
        logError('[useCollaboration] WebSocket error:', error)
        isConnectingRef.current = false
        setIsConnecting(false)
        onError?.('WebSocket connection error')
      }

      ws.onclose = (event) => {
        logDebug('[useCollaboration] WebSocket closed:', event.code, event.reason)
        isConnectingRef.current = false
        setIsConnected(false)
        setIsConnecting(false)
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current)
          pingIntervalRef.current = null
        }
        
        // Clear any pending reconnection
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
          reconnectTimeoutRef.current = null
        }
        
        // Attempt reconnection if not intentional close and reconnection is enabled
        if (event.code !== 1000 && enabled && shouldReconnectRef.current && documentId) {
          logDebug('[useCollaboration] Attempting to reconnect in 3 seconds...')
          reconnectTimeoutRef.current = setTimeout(() => {
            // Only reconnect if still enabled and not already connecting
            if (shouldReconnectRef.current && enabled && !isConnectingRef.current && documentId) {
              connect()
            }
          }, 3000)
        }
      }
    } catch (error) {
      logError('[useCollaboration] Error creating WebSocket:', error)
      isConnectingRef.current = false
      setIsConnecting(false)
      onError?.('Failed to create WebSocket connection')
    }
  }, [documentId, enabled, getWebSocketUrl, onError, handleMessage])

  // Send document change
  const sendChange = useCallback((
    change: { type: 'insert' | 'delete'; position: number; length: number; content?: string },
    content: string
  ) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'change',
        change: {
          type: change.type,
          position: change.position,
          length: change.length,
          content: change.content,
          version: 0 // Will be set by server
        },
        content: content
      }))
    } else {
      logWarn('[useCollaboration] Cannot send change - WebSocket not connected')
    }
  }, [])

  // Send cursor position update
  const sendCursorUpdate = useCallback((position: number) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cursor',
        position: position
      }))
    }
  }, [])

  // Disconnect WebSocket
  const disconnect = useCallback(() => {
    // Disable reconnection when manually disconnecting
    shouldReconnectRef.current = false
    
    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    
    // Clear ping interval
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current)
      pingIntervalRef.current = null
    }
    
    // Close WebSocket
    if (wsRef.current) {
      wsRef.current.close(1000, 'Intentional disconnect')
      wsRef.current = null
    }
    
    isConnectingRef.current = false
    setIsConnected(false)
    setIsConnecting(false)
    setActiveUsers([])
  }, [])

  // Connect when documentId changes or component mounts
  useEffect(() => {
    mountedRef.current = true
    const connectionKey = `${documentId}-${enabled}`
    
    // If connection key changed, disconnect old connection first
    if (connectionKeyRef.current && connectionKeyRef.current !== connectionKey) {
      logDebug('[useCollaboration] Connection key changed, disconnecting old connection')
      shouldReconnectRef.current = false
      if (wsRef.current) {
        disconnect()
      }
    }
    
    connectionKeyRef.current = connectionKey
    
    // Clear any pending reconnection immediately
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
      reconnectTimeoutRef.current = null
    }
    
    // SECURITY: No longer check for token - cookies are sent automatically
    if (documentId && enabled) {
      // Only connect if we don't already have a valid connection for this key
      const currentState = wsRef.current?.readyState
      if (!wsRef.current || currentState === WebSocket.CLOSED || currentState === undefined) {
        // Reset reconnection flag for new connection
        shouldReconnectRef.current = true
        connect()
      } else {
        logDebug('[useCollaboration] Already have active connection, skipping new connection')
      }
    } else {
      // If conditions not met, ensure we're disconnected
      shouldReconnectRef.current = false
      if (wsRef.current) {
        disconnect()
      }
    }
    
    // Cleanup function
    return () => {
      mountedRef.current = false
      
      // Only cleanup if this is the current connection key
      if (connectionKeyRef.current === connectionKey) {
        logDebug('[useCollaboration] Cleaning up connection for key:', connectionKey)
        shouldReconnectRef.current = false
        // Clear any pending reconnection
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current)
          reconnectTimeoutRef.current = null
        }
        // Disconnect if component unmounting or conditions no longer met
        if (!documentId || !enabled) {
          disconnect()
        } else if (wsRef.current) {
          // Component unmounting but conditions still met - close connection
          // This handles React StrictMode double-mounting
          logDebug('[useCollaboration] Component unmounting, closing connection')
          const ws = wsRef.current
          wsRef.current = null
          try {
            ws.close(1000, 'Component unmounting')
          } catch (e) {
            // Ignore errors
          }
          isConnectingRef.current = false
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, enabled]) // Only depend on documentId and enabled, not on connect/disconnect

  return {
    isConnected,
    isConnecting,
    activeUsers,
    sendChange,
    sendCursorUpdate,
    disconnect
  }
}

